import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Upload, X, GripVertical } from 'lucide-react';
import { galleryAPI, uploadAPI } from '@/lib/api';

const AdminGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    id: '',
    title_fa: '',
    image_url: '',
    description_fa: '',
    order: 0,
    created_at: '',
  });

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await galleryAPI.getAll();
      setImages(response.data);
    } catch (error) {
      console.error('Failed to fetch gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      // Make sure URL is complete
      let imageUrl = response.data.url;
      if (imageUrl.startsWith('/')) {
        // Relative URL, make it absolute
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        imageUrl = `${backendUrl}${imageUrl}`;
      }
      setFormData({ ...formData, image_url: imageUrl });
    } catch (error) {
      console.error('Upload failed:', error);
      alert('آپلود تصویر با خطا مواجه شد');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingImage) {
        await galleryAPI.update(formData.id, formData);
        alert('تصویر با موفقیت به‌روزرسانی شد');
      } else {
        const newId = `gallery_${Date.now()}`;
        const newImage = {
          ...formData,
          id: newId,
          created_at: new Date().toISOString(),
          order: images.length,
        };
        await galleryAPI.create(newImage);
        alert('تصویر با موفقیت اضافه شد');
      }
      
      fetchImages();
      closeModal();
    } catch (error) {
      console.error('Failed to save image:', error);
      alert('خطا در ذخیره تصویر');
    }
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormData(image);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این تصویر اطمینان دارید؟')) return;

    try {
      await galleryAPI.delete(id);
      alert('تصویر با موفقیت حذف شد');
      fetchImages();
    } catch (error) {
      console.error('Failed to delete image:', error);
      alert('خطا در حذف تصویر');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingImage(null);
    setFormData({
      id: '',
      title_fa: '',
      image_url: '',
      description_fa: '',
      order: 0,
      created_at: '',
    });
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen"><div className="text-[#D4AF37]">در حال بارگذاری...</div></div>;
  }

  return (
    <div className="min-h-screen bg-black p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">گالری سمینارها</h1>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black rounded-lg hover:bg-[#F3E5AB] transition-colors"
          >
            <Plus size={20} />
            افزودن تصویر جدید
          </button>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {images.map((image) => (
            <div key={image.id} className="glass-effect border border-white/10 rounded-xl overflow-hidden group">
              <div className="relative h-48">
                <img
                  src={image.image_url}
                  alt={image.title_fa}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => handleEdit(image)}
                    className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Edit2 size={20} />
                  </button>
                  <button
                    onClick={() => handleDelete(image.id)}
                    className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-white font-medium mb-1">{image.title_fa}</h3>
                {image.description_fa && (
                  <p className="text-sm text-zinc-400 line-clamp-2">{image.description_fa}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {images.length === 0 && (
          <div className="text-center text-zinc-400 py-12">
            هنوز تصویری اضافه نشده است. برای افزودن تصویر جدید روی دکمه بالا کلیک کنید.
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingImage ? 'ویرایش تصویر' : 'افزودن تصویر جدید'}
                </h2>
                <button onClick={closeModal} className="text-zinc-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">تصویر *</label>
                  {formData.image_url ? (
                    <div className="relative">
                      <img src={formData.image_url} alt="Preview" className="w-full h-64 object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image_url: '' })}
                        className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-zinc-700 rounded-lg p-12 text-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="gallery-image"
                      />
                      <label htmlFor="gallery-image" className="cursor-pointer">
                        <Upload className="mx-auto mb-2 text-zinc-400" size={48} />
                        <p className="text-sm text-zinc-400">
                          {uploading ? 'در حال آپلود...' : 'کلیک کنید یا تصویر را بکشید'}
                        </p>
                      </label>
                    </div>
                  )}
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">عنوان *</label>
                  <input
                    type="text"
                    value={formData.title_fa}
                    onChange={(e) => setFormData({ ...formData, title_fa: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                    placeholder="مثال: سمینار مهاجرت به کانادا"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">توضیحات (اختیاری)</label>
                  <textarea
                    value={formData.description_fa}
                    onChange={(e) => setFormData({ ...formData, description_fa: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#D4AF37] min-h-[100px]"
                    placeholder="توضیحات کوتاه درباره تصویر..."
                  />
                </div>

                {/* Buttons */}
                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-[#D4AF37] text-black rounded-lg hover:bg-[#F3E5AB] transition-colors font-medium"
                  >
                    {editingImage ? 'به‌روزرسانی' : 'ایجاد'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-6 py-3 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors"
                  >
                    انصراف
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminGallery;
